'use client'

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import axios from "axios";

/* ================================
   CONFIG
================================ */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;


/* ================================
   AXIOS INSTANCE
================================ */
export const api = axios.create({
  baseURL: API,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  }
});

const addNoCacheHeaders = (config) => {
  if (String(config.method || "get").toLowerCase() === "get") {
    config.headers = config.headers || {};
    config.headers["Cache-Control"] = "no-cache";
    config.headers.Pragma = "no-cache";
    config.headers.Expires = "0";
    config.params = {
      ...(config.params || {}),
      _ts: Date.now(),
    };
  }

  return config;
};

axios.interceptors.request.use(addNoCacheHeaders);

api.interceptors.request.use((config) => {
  addNoCacheHeaders(config);
  const token = localStorage.getItem("token");

  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});


// ✅ Response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/* ================================
   CONTEXT
================================ */
const AuthContext = createContext(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

/* ================================
   PROVIDER
================================ */
export const AuthProvider = ({ children }) => {
  // ✅ Initialize token immediately from localStorage
  const [token, setToken] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        return localStorage.getItem("token") || null;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // const hasFetched = useRef(false);
useEffect(() => {
  const initAuth = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  initAuth();
}, [token]);



  /* ================================
     LOGIN
  ================================ */
const login = async (mobile, password) => {
  const res = await api.post("/auth/login", { mobile, password });

  const token = res.data.access_token;
  if (!token) {
    console.error("TOKEN IS MISSING FROM RESPONSE");
    return;
  }

  localStorage.setItem("token", token);
  setToken(token);
  
  // Fetch fresh user data after login to get all fields including mobile
  try {
    const userRes = await api.get("/auth/me");
    setUser(userRes.data);
    return userRes.data;
  } catch (error) {
    console.error("Failed to fetch user data after login", error);
    setUser(res.data.user);
    return res.data.user;
  }
};


  /* ================================
     REGISTER
  ================================ */
const register = async (mobile, password, name) => {
  const res = await api.post("/auth/register", {
    mobile,
    password,
    name,
  });

  const token = res.data.access_token;
  if (!token) {
    console.error("TOKEN IS MISSING FROM RESPONSE");
    return;
  }

  localStorage.setItem("token", token);
  setToken(token);
  
  // Fetch fresh user data after registration to get all fields including mobile
  try {
    const userRes = await api.get("/auth/me");
    setUser(userRes.data);
    return userRes.data;
  } catch (error) {
    console.error("Failed to fetch user data after registration", error);
    setUser(res.data.user);
    return res.data.user;
  }
};



  /* ================================
     LOGOUT
  ================================ */
  const logout = () => {
  localStorage.removeItem("token");
  setToken(null);
  setUser(null);
};

  const googleLogin = async (idToken) => {
    const res = await api.post("/auth/google", { token: idToken });
    const newToken = res.data.access_token;
    if (!newToken) throw new Error("No token received");
    localStorage.setItem("token", newToken);
    setToken(newToken);
    
    // Fetch fresh user data after Google login to get all fields including mobile
    try {
      const userRes = await api.get("/auth/me");
      setUser(userRes.data);
      return userRes.data;
    } catch (error) {
      console.error("Failed to fetch user data after Google login", error);
      setUser(res.data.user);
      return res.data.user;
    }
  };

  const refreshUser = async () => {
    if (!token) return null;
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        googleLogin,
        refreshUser,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
